/**
 * Name normalization and Cognito user matching utilities.
 *
 * Used by the project creation wizard (Step 4) to resolve Excel person names
 * (e.g. "Liesbeth Cogge") to Cognito user accounts.
 */

import type { TesterUser } from '@/services/testers';

export type MatchConfidence = 'exact' | 'fuzzy' | 'manual' | null;

export interface ResolvedPerson {
  excelName: string;
  excelEmail: string | null;
  cognitoUserId: string | null;
  cognitoUserName: string | null;
  cognitoEmail: string | null;
  matchConfidence: MatchConfidence;
}

/**
 * Strip diacritics, lowercase, collapse whitespace.
 * "Liesbeth Cogge" → "liesbeth cogge"
 * "José García" → "jose garcia"
 */
export function normalizeName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Resolve a single Excel person name to a Cognito user.
 *
 * Priority:
 * 0. Exact email match (case-insensitive) — highest confidence
 * 1. Exact normalized match on full name
 * 2. Unique last-name match (only if exactly one Cognito user shares the last name)
 * 3. null (unresolved)
 */
export function resolvePersonName(
  excelName: string,
  cognitoUsers: TesterUser[],
  excelEmail?: string | null,
): ResolvedPerson {
  const base: ResolvedPerson = {
    excelName,
    excelEmail: excelEmail ?? null,
    cognitoUserId: null,
    cognitoUserName: null,
    cognitoEmail: null,
    matchConfidence: null,
  };

  if (!excelName.trim() || cognitoUsers.length === 0) return base;

  // 0. Email-exact match (case-insensitive, supports ";" separated multi-emails)
  if (excelEmail?.trim()) {
    const emails = excelEmail.includes(';')
      ? excelEmail.split(';').map((e) => e.trim()).filter(Boolean)
      : [excelEmail.trim()];
    for (const email of emails) {
      const normalizedEmail = email.toLowerCase();
      const emailMatch = cognitoUsers.find(
        (u) => u.email?.toLowerCase() === normalizedEmail,
      );
      if (emailMatch) {
        return {
          excelName,
          excelEmail: excelEmail ?? null,
          cognitoUserId: emailMatch.id,
          cognitoUserName: emailMatch.name,
          cognitoEmail: emailMatch.email,
          matchConfidence: 'exact',
        };
      }
    }
  }

  const normalizedExcel = normalizeName(excelName);

  // 1. Exact normalized match
  const exactMatch = cognitoUsers.find(
    (u) => normalizeName(u.name) === normalizedExcel,
  );
  if (exactMatch) {
    return {
      excelName,
      excelEmail: excelEmail ?? null,
      cognitoUserId: exactMatch.id,
      cognitoUserName: exactMatch.name,
      cognitoEmail: exactMatch.email,
      matchConfidence: 'exact',
    };
  }

  // 2. Unique last-name match
  const excelParts = normalizedExcel.split(' ');
  const excelLastName = excelParts[excelParts.length - 1];
  if (excelLastName.length >= 2) {
    const lastNameMatches = cognitoUsers.filter((u) => {
      const parts = normalizeName(u.name).split(' ');
      return parts[parts.length - 1] === excelLastName;
    });
    if (lastNameMatches.length === 1) {
      return {
        excelName,
        excelEmail: excelEmail ?? null,
        cognitoUserId: lastNameMatches[0].id,
        cognitoUserName: lastNameMatches[0].name,
        cognitoEmail: lastNameMatches[0].email,
        matchConfidence: 'fuzzy',
      };
    }
  }

  // 3. No match
  return base;
}

/**
 * Batch resolve all person names against Cognito user list.
 * Returns a Map<excelName, ResolvedPerson> for O(1) lookup.
 */
export function batchResolvePersons(
  persons: Array<{ name: string; email?: string | null }>,
  cognitoUsers: TesterUser[],
): Map<string, ResolvedPerson> {
  const result = new Map<string, ResolvedPerson>();
  const seen = new Set<string>();

  for (const person of persons) {
    if (seen.has(person.name)) continue;
    seen.add(person.name);
    result.set(person.name, resolvePersonName(person.name, cognitoUsers, person.email));
  }

  return result;
}
